import { Routes } from '@angular/router';
import { SeoData } from './core/services/seo.service';
import { localizedLanguageGuard } from './core/guards/language-route.guard';

function seo(title: string, description: string, extras?: Omit<SeoData, 'title' | 'description'>): { seo: SeoData } {
  return { seo: { title, description, ...extras } };
}

const pageRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/home3/home3.component').then((m) => m.Home3Component),
    title: 'Hébergement cloud : VPS et serveurs en France | Ancelade',
    data: seo(
      'Hébergement cloud : VPS et serveurs en France | Ancelade',
      'VPS, serveurs dédiés et hébergement web Plesk en France, protection anti-DDoS incluse. Comparez les VPS Intel et AMD Ryzen et gérez vos services en ligne.',
      { name: 'Accueil', pageType: 'WebPage' }
    ),
  },
  {
    // Nouvelle page d'accueil en cours de validation : servie et prérendue, mais hors sitemap et noindex.
    path: 'home2',
    loadComponent: () => import('./pages/home2/home2.component').then((m) => m.Home2Component),
    title: 'Hébergeur cloud français : VPS, serveurs et web | Ancelade',
    data: seo(
      'Hébergeur cloud français : VPS, serveurs et web | Ancelade',
      'VPS, serveurs dédiés et hébergement web opérés en région parisienne. Comparez les offres, vérifiez un nom de domaine et gérez vos services en ligne.',
      { name: 'Accueil', pageType: 'WebPage', noindex: true }
    ),
  },
  {
    path: 'vps-standard',
    loadComponent: () => import('./pages/v3/vps-v3/vps-v3.component').then((m) => m.VpsV3Component),
    title: 'VPS Standard Intel SSD : Linux et Windows | Ancelade',
    data: {
      range: 'standard',
      ...seo(
        'VPS Standard Intel SSD : Linux et Windows | Ancelade',
        'Hébergez vos applications sur un VPS Intel avec stockage SSD. Comparez les configurations LXC, KVM Linux ou Windows et les ressources disponibles.',
        { name: 'VPS Standard', pageType: 'CollectionPage', serviceType: 'VPS Standard' }
      ),
    },
  },
  {
    path: 'vps-intel',
    redirectTo: 'vps-standard',
    pathMatch: 'full',
  },
  {
    path: 'vps-ryzen',
    redirectTo: 'vps-performance',
    pathMatch: 'full',
  },
  {
    path: 'plesk',
    loadComponent: () => import('./pages/v3/plesk-v3/plesk-v3.component').then((m) => m.PleskV3Component),
    title: 'Hébergement web Plesk : sites, e-mails et bases | Ancelade',
    data: seo(
      'Hébergement web Plesk : sites, e-mails et bases | Ancelade',
      'Publiez vos sites sur une plateforme Plesk administrée par Ancelade. Gérez domaines, e-mails, bases de données et certificats depuis votre panneau.',
      { name: 'Hébergement Plesk', pageType: 'CollectionPage', serviceType: 'Hébergement Plesk' }
    ),
  },
  {
    path: 'plesk-dedicated',
    loadComponent: () =>
      import('./pages/v3/plesk-dedicated-v3/plesk-dedicated-v3.component').then((m) => m.PleskDedicatedV3Component),
    title: 'VPS avec Plesk préinstallé et accès root | Ancelade',
    data: seo(
      'VPS avec Plesk préinstallé et accès root | Ancelade',
      'Configurez un VPS KVM Linux avec Plesk et accès root. Comparez Intel SSD et AMD Ryzen NVMe, puis choisissez l’édition Plesk adaptée à vos domaines.',
      { name: 'Plesk sur VPS dédié', pageType: 'CollectionPage', serviceType: 'Plesk sur VPS dédié' }
    ),
  },
  {
    path: 'domains',
    loadComponent: () => import('./pages/v3/domains-v3/domains-v3.component').then((m) => m.DomainsV3Component),
    title: 'Nom de domaine : recherche, transfert et DNS | Ancelade',
    data: seo(
      'Nom de domaine : recherche, transfert et DNS | Ancelade',
      'Recherchez un nom de domaine, comparez les tarifs d’enregistrement et de renouvellement, préparez un transfert et gérez vos enregistrements DNS.',
      { name: 'Noms de domaine', pageType: 'CollectionPage', serviceType: 'Noms de domaine' }
    ),
  },
  {
    path: 'datacenter',
    loadComponent: () =>
      import('./pages/v3/datacenter-v3/datacenter-v3.component').then((m) => m.DatacenterV3Component),
    title: 'Colocation datacenter en région parisienne | Ancelade',
    data: seo(
      'Colocation datacenter en région parisienne | Ancelade',
      'Hébergez vos équipements en région parisienne, de l’unité rack à la baie complète. Configurez l’espace, l’alimentation et le réseau pour votre devis.',
      { name: 'Colocation datacenter', pageType: 'CollectionPage', serviceType: 'Colocation datacenter' }
    ),
  },
  {
    path: 'vps-performance',
    loadComponent: () => import('./pages/v3/vps-v3/vps-v3.component').then((m) => m.VpsV3Component),
    title: 'VPS AMD Ryzen avec stockage NVMe RAID 10 | Ancelade',
    data: {
      range: 'performance',
      ...seo(
        'VPS AMD Ryzen avec stockage NVMe RAID 10 | Ancelade',
        'Découvrez les VPS AMD Ryzen avec stockage NVMe RAID 10. Choisissez vos vCPU, votre mémoire et votre environnement LXC, KVM Linux ou Windows.',
        { name: 'VPS Performance', pageType: 'CollectionPage', serviceType: 'VPS Performance' }
      ),
    },
  },
  {
    path: 'ip-tunnels',
    loadComponent: () => import('./pages/v3/ip-tunnels-v3/ip-tunnels-v3.component').then((m) => m.IpTunnelsV3Component),
    title: 'Tunnels IP EoIP et VXLAN : reliez vos réseaux | Ancelade',
    data: seo(
      'Tunnels IP EoIP et VXLAN : reliez vos réseaux | Ancelade',
      'Interconnectez vos sites avec un tunnel EoIP ou VXLAN. Définissez le débit, les adresses IP ou la session BGP et préparez votre demande de devis.',
      { name: 'Tunnels IP', pageType: 'CollectionPage', serviceType: 'Tunnels IP' }
    ),
  },
  {
    path: 'antiddos',
    loadComponent: () => import('./pages/v3/antiddos-v3/antiddos-v3.component').then((m) => m.AntiddosV3Component),
    title: 'Protection anti-DDoS L3/L4 pour VPS et serveurs | Ancelade',
    data: seo(
      'Protection anti-DDoS L3/L4 pour VPS et serveurs | Ancelade',
      'Comprenez le filtrage anti-DDoS permanent L3/L4 de vos VPS et serveurs. Consultez les protocoles concernés et le périmètre de la protection réseau.',
      { name: 'Protection anti-DDoS', pageType: 'CollectionPage', serviceType: 'Protection anti-DDoS' }
    ),
  },
  {
    path: 'dedicated',
    loadComponent: () => import('./pages/v3/dedicated-v3/dedicated-v3.component').then((m) => m.DedicatedV3Component),
    title: 'Serveurs dédiés : configurations et accès root | Ancelade',
    data: seo(
      'Serveurs dédiés : configurations et accès root | Ancelade',
      'Comparez les serveurs physiques dédiés avec accès administrateur. Choisissez vos ressources ou décrivez votre charge pour une configuration sur devis.',
      { name: 'Serveurs dédiés', pageType: 'CollectionPage', serviceType: 'Serveurs dédiés' }
    ),
  },
  {
    path: 'telephonie',
    loadComponent: () =>
      import('./pages/v3/telephonie-v3/telephonie-v3.component').then((m) => m.TelephonieV3Component),
    title: 'Téléphonie SIP : trunk, numéros et standard IPBX | Ancelade',
    data: seo(
      'Téléphonie SIP : trunk, numéros et standard IPBX | Ancelade',
      'Découvrez les offres de téléphonie SIP, les lignes et les fonctions de standard IPBX. Préparez vos besoins en numéros, postes et appels pour un devis.',
      { name: 'Téléphonie SIP', pageType: 'CollectionPage', serviceType: 'Téléphonie SIP' }
    ),
  },
  {
    path: 'workspace',
    loadComponent: () => import('./pages/v3/workspace-v3/workspace-v3.component').then((m) => m.WorkspaceV3Component),
    title: 'Microsoft 365 et Google Workspace pour équipes | Ancelade',
    data: seo(
      'Microsoft 365 et Google Workspace pour équipes | Ancelade',
      'Comparez Microsoft 365 et Google Workspace pour votre équipe : messagerie, applications et stockage. Consultez les licences et les engagements.',
      {
        name: 'Microsoft 365 et Google Workspace',
        pageType: 'CollectionPage',
        serviceType: 'Microsoft 365 et Google Workspace',
      }
    ),
  },
  {
    path: 'fibre',
    loadComponent: () => import('./pages/v3/fibre-v3/fibre-v3.component').then((m) => m.FibreV3Component),
    title: 'Fibre entreprise : FTTH Pro et FTTO dédié | Ancelade',
    data: seo(
      'Fibre entreprise : FTTH Pro et FTTO dédié | Ancelade',
      'Comparez la fibre FTTH Pro mutualisée et la FTTO dédiée. Précisez l’adresse de votre site et vos besoins de débit pour une étude d’éligibilité.',
      { name: 'Fibre entreprise', pageType: 'CollectionPage', serviceType: 'Fibre entreprise' }
    ),
  },
  {
    path: 'ssl',
    loadComponent: () => import('./pages/v3/ssl-v3/ssl-v3.component').then((m) => m.SslV3Component),
    title: 'Certificats SSL Let’s Encrypt avec Plesk | Ancelade',
    data: seo(
      'Certificats SSL Let’s Encrypt avec Plesk | Ancelade',
      'Activez HTTPS sur vos sites avec les certificats Let’s Encrypt inclus dans Plesk. Découvrez la validation des domaines et le renouvellement automatique.',
      { name: 'Certificats SSL', pageType: 'CollectionPage', serviceType: 'Certificats SSL' }
    ),
  },
  {
    path: 'status',
    loadComponent: () => import('./pages/v3/status-v3/status-v3.component').then((m) => m.StatusV3Component),
    title: 'État des services : incidents et maintenances | Ancelade',
    data: seo(
      'État des services : incidents et maintenances | Ancelade',
      'Consultez l’état des services, les incidents en cours, les maintenances planifiées et leur historique. Suivez les mises à jour de la plateforme.',
      { name: 'État des services', pageType: 'WebPage' }
    ),
  },
  {
    path: 'contact',
    loadComponent: () => import('./pages/v3/contact-v3/contact-v3.component').then((m) => m.ContactV3Component),
    title: 'Contact hébergement : conseil et devis technique | Ancelade',
    data: seo(
      'Contact hébergement : conseil et devis technique | Ancelade',
      'Décrivez votre projet d’hébergement, de réseau ou de migration. Précisez vos applications et vos contraintes pour préparer une réponse ou un devis.',
      { name: 'Contact', pageType: 'ContactPage' }
    ),
  },
  {
    path: 'about',
    loadComponent: () => import('./pages/v3/about-v3/about-v3.component').then((m) => m.AboutV3Component),
    title: 'Hébergeur cloud français : notre infrastructure | Ancelade',
    data: seo(
      'Hébergeur cloud français : notre infrastructure | Ancelade',
      'Découvrez Ancelade, hébergeur français qui opère des serveurs et un réseau en région parisienne. Comprenez notre infrastructure et l’accompagnement proposé.',
      { name: 'À propos', pageType: 'AboutPage' }
    ),
  },
  // Guides : réponses aux questions d’achat, à partir des faits publiés sur les pages d’offres.
  {
    path: 'guide-vps',
    loadComponent: () => import('./pages/v3/guide-v3/guide-v3.component').then((m) => m.GuideV3Component),
    title: 'VPS LXC, KVM ou Windows : lequel choisir ? | Ancelade',
    data: {
      guide: 'guide-vps',
      ...seo(
        'VPS LXC, KVM ou Windows : lequel choisir ? | Ancelade',
        'Conteneur LXC, machine virtuelle KVM ou Windows Server : comparez les usages, les limites et ce qui est inclus pour choisir votre VPS en France.',
        { name: 'Guide VPS : LXC, KVM ou Windows', pageType: 'WebPage' }
      ),
    },
  },
  {
    path: 'guide-plesk',
    loadComponent: () => import('./pages/v3/guide-v3/guide-v3.component').then((m) => m.GuideV3Component),
    title: 'Hébergement Plesk ou VPS avec Plesk : que choisir ? | Ancelade',
    data: {
      guide: 'guide-plesk',
      ...seo(
        'Hébergement Plesk ou VPS avec Plesk : que choisir ? | Ancelade',
        'Plateforme Plesk gérée par Ancelade ou VPS avec Plesk et accès root : ressources, administration, sauvegardes et licence comparées.',
        { name: 'Guide : hébergement Plesk ou VPS', pageType: 'WebPage' }
      ),
    },
  },
  {
    path: 'guide-antiddos',
    loadComponent: () => import('./pages/v3/guide-v3/guide-v3.component').then((m) => m.GuideV3Component),
    title: 'Anti-DDoS L3/L4 : ce qui est couvert ou non | Ancelade',
    data: {
      guide: 'guide-antiddos',
      ...seo(
        'Anti-DDoS L3/L4 : ce qui est couvert ou non | Ancelade',
        'Filtrage eBPF/XDP, attaques réseau et par amplification filtrées, serveurs de jeu et limites de la protection anti-DDoS incluse sur nos VPS.',
        { name: 'Guide anti-DDoS L3/L4', pageType: 'WebPage' }
      ),
    },
  },
  {
    path: 'guide-colocation',
    loadComponent: () => import('./pages/v3/guide-v3/guide-v3.component').then((m) => m.GuideV3Component),
    title: 'Prix d’une colocation datacenter : le calcul | Ancelade',
    data: {
      guide: 'guide-colocation',
      ...seo(
        'Prix d’une colocation datacenter : le calcul | Ancelade',
        'Espace en U, puissance et débit engagé : le barème de la colocation chez Equinix PA6, avec des exemples de prix TTC par mois.',
        { name: 'Guide : prix de la colocation', pageType: 'WebPage' }
      ),
    },
  },
  {
    path: 'mentions-legales',
    loadComponent: () =>
      import('./pages/v3/mentions-legales-v3/mentions-legales-v3.component').then((m) => m.MentionsLegalesV3Component),
    title: 'Mentions légales et informations sur la société | Ancelade',
    data: seo(
      'Mentions légales et informations sur la société | Ancelade',
      'Consultez les informations légales sur Ancelade, l’éditeur du site, ses coordonnées et les responsabilités associées à l’utilisation du site.',
      { name: 'Mentions légales', pageType: 'WebPage' }
    ),
  },
  {
    path: 'confidentialite',
    loadComponent: () =>
      import('./pages/v3/confidentialite-v3/confidentialite-v3.component').then((m) => m.ConfidentialiteV3Component),
    title: 'Politique de confidentialité et données personnelles | Ancelade',
    data: seo(
      'Politique de confidentialité et données personnelles | Ancelade',
      'Consultez les informations sur les données personnelles, les cookies, les finalités de traitement et les moyens de contacter Ancelade pour exercer vos droits.',
      { name: 'Confidentialité', pageType: 'WebPage' }
    ),
  },
  {
    path: 'cgu',
    loadComponent: () => import('./pages/v3/cgu-v3/cgu-v3.component').then((m) => m.CguV3Component),
    title: 'Conditions générales d’utilisation et de vente | Ancelade',
    data: seo(
      'Conditions générales d’utilisation et de vente | Ancelade',
      'Consultez les conditions d’accès au site, de gestion du compte client, de commande et d’utilisation des services d’hébergement et de réseau Ancelade.',
      { name: 'Conditions générales', pageType: 'WebPage' }
    ),
  },
  {
    // Référence de l'API client : page cachée (aucun lien, hors sitemap, noindex), partagée au cas par cas.
    path: 'api',
    loadComponent: () =>
      import('./pages/v3/api-reference-v3/api-reference-v3.component').then((m) => m.ApiReferenceV3Component),
    title: 'Référence de l’API client | Ancelade',
    data: seo(
      'Référence de l’API client | Ancelade',
      'Routes de l’API Ancelade ouvertes aux clients : paramètres, réponses, exemples de code cURL, PHP, Python et C#, et console pour tester vos requêtes.',
      { name: 'Référence API', pageType: 'WebPage', noindex: true }
    ),
  },
  {
    // Pages de comparaison du nouveau design, remplacées par les pages publiques à la bascule (octobre 2026).
    path: 'home3',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: '**',
    loadComponent: () => import('./pages/v3/not-found-v3/not-found-v3.component').then((m) => m.NotFoundV3Component),
    title: 'Page introuvable | Ancelade',
    data: seo(
      'Page introuvable | Ancelade',
      'Cette page n’existe pas ou a été déplacée. Retrouvez les hébergements et les services Ancelade depuis l’accueil.',
      { name: 'Page introuvable', pageType: 'WebPage', noindex: true }
    ),
  },
];

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => import('./shared/layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [pageRoutes[0]],
  },
  {
    path: ':lang',
    canActivate: [localizedLanguageGuard],
    loadComponent: () => import('./shared/layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: pageRoutes,
  },
];
